/**
 * Screen 10 — Add money.
 *
 * Big amount display + quick-amount pills + payment method picker. Idempotency
 * key is stable for the lifetime of the form so an accidental double-tap reuses
 * the same txn.
 *
 * Only Paystack (card + bank transfer) is wired right now; the USSD and mobile
 * money rows are visible in the picker but selecting one explains that the
 * method will arrive in a later release.
 */

import { api, ApiError } from '../api.js';
import { setStatus, statusZone } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo, nairaToKobo } from '../wallet.js';

const STATUS_ID = 'deposit-status';
const PRESETS = [500, 1000, 2000, 5000]; // naira

const METHODS = [
  { id: 'paystack-card', label: 'Debit card', sub: 'Paystack', provider: 'paystack' },
  { id: 'paystack-transfer', label: 'Bank transfer', sub: 'Paystack', provider: 'paystack' },
  { id: 'ussd', label: 'USSD', sub: '*737#', provider: null },
  { id: 'momo', label: 'Mobile money', sub: 'OPay / Momo', provider: null },
];

export function renderDeposit(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/wallet" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Add money</div>
    </div>
    <div class="pad">
      <div class="center" style="margin:10px 0 2px;color:var(--mute);font-size:11px;font-weight:600;letter-spacing:0.5px;">
        ENTER AMOUNT
      </div>
      <div class="center big" id="amount-display" style="font-size:38px;font-weight:700;">₦0</div>

      <input type="hidden" id="amount-naira" value="0" />

      <div class="center" style="margin:14px 0;" id="preset-pills">
        ${PRESETS.map((p) =>
          `<span class="pill" data-naira="${p}">₦${p.toLocaleString('en-NG')}</span>`,
        ).join('')}
        <span class="pill" data-custom="1">Other</span>
      </div>

      <div id="custom-amount" style="display:none;margin:10px 0;">
        <label class="field">
          <span class="field-label">Custom amount (NGN)</span>
          <input class="field-input" type="number" inputmode="numeric" min="100" step="100" id="custom-input" placeholder="e.g. 1500" />
        </label>
      </div>

      <h3 class="sec">Pay with</h3>
      <div id="method-list">
        ${METHODS.map(methodRowHtml).join('')}
      </div>

      ${statusZone(STATUS_ID)}

      <button type="button" class="btn gold" id="pay-btn" disabled>Pay ₦0</button>
      <div class="center" style="font-size:11px;color:var(--mute);font-weight:500;">
        Secured by Paystack
      </div>
    </div>
  `;

  // State held in the DOM via data-*; reading from the input avoids any
  // separate JS source-of-truth drifting from what the user sees.
  let selectedMethod = METHODS[0];
  let amountNaira = 0;

  function refresh() {
    document.getElementById('amount-display').textContent =
      `₦${amountNaira.toLocaleString('en-NG')}`;
    const btn = document.getElementById('pay-btn');
    btn.textContent = amountNaira > 0
      ? `Pay ₦${amountNaira.toLocaleString('en-NG')}`
      : 'Pay ₦0';
    btn.disabled = !(amountNaira >= 100 && selectedMethod.provider);
  }

  document.getElementById('preset-pills').addEventListener('click', (e) => {
    const pill = e.target.closest('.pill');
    if (!pill) return;
    const naira = pill.dataset.naira;
    if (naira) {
      amountNaira = parseInt(naira, 10);
      document.getElementById('custom-amount').style.display = 'none';
      // Visually mark the selected preset.
      document.querySelectorAll('#preset-pills .pill').forEach((p) =>
        p.classList.toggle('on', p === pill),
      );
    } else if (pill.dataset.custom) {
      document.getElementById('custom-amount').style.display = 'block';
      document.getElementById('custom-input').focus();
      document.querySelectorAll('#preset-pills .pill').forEach((p) =>
        p.classList.toggle('on', p === pill),
      );
    }
    refresh();
  });

  document.getElementById('custom-input').addEventListener('input', (e) => {
    const v = parseInt(e.target.value || '0', 10);
    amountNaira = Number.isFinite(v) ? v : 0;
    refresh();
  });

  document.getElementById('method-list').addEventListener('click', (e) => {
    const row = e.target.closest('.opt');
    if (!row) return;
    selectedMethod = METHODS.find((m) => m.id === row.dataset.method) || selectedMethod;
    document.querySelectorAll('#method-list .opt').forEach((r) => {
      const on = r === row;
      r.classList.toggle('violet-on', on);
      r.querySelector('.radio')?.classList.toggle('on', on);
    });
    if (!selectedMethod.provider) {
      setStatus(STATUS_ID, {
        message: `${selectedMethod.label} is coming soon — pick a card or transfer for now.`,
        kind: 'info',
      });
    } else {
      setStatus(STATUS_ID, { message: '' });
    }
    refresh();
  });

  // Stable idempotency key per form mount.
  const idempotencyKey = `dep-${crypto.randomUUID()}`;

  document.getElementById('pay-btn').addEventListener('click', async () => {
    if (!selectedMethod.provider) return;
    const amountKobo = nairaToKobo(amountNaira);
    if (!(amountKobo >= 10_000 && amountKobo % 100 === 0)) {
      setStatus(STATUS_ID, { message: 'Minimum ₦100, whole naira only.', kind: 'error' });
      return;
    }
    setStatus(STATUS_ID, { message: 'Starting deposit…', kind: 'info' });
    const btn = document.getElementById('pay-btn');
    btn.disabled = true;
    try {
      const res = await api('/wallet/deposit/init', {
        method: 'POST',
        body: {
          amount_kobo: amountKobo,
          provider: selectedMethod.provider,
          idempotency_key: idempotencyKey,
        },
      });
      toast.info(`Redirecting to ${selectedMethod.sub} for ${formatKobo(amountKobo)}…`);
      window.location.href = res.checkout_url;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
      const msg = err instanceof ApiError ? err.message : 'Could not start deposit.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
      btn.disabled = false;
    }
  });
}

function methodRowHtml(m, i) {
  const on = i === 0;
  return `
    <div class="opt ${on ? 'violet-on' : ''}" data-method="${m.id}">
      <span>${m.label}</span>
      <div class="row" style="gap:9px;">
        <span style="font-size:11px;color:var(--mute);font-weight:600;">${m.sub}</span>
        <span class="radio ${on ? 'on' : ''}"></span>
      </div>
    </div>
  `;
}

/**
 * `/wallet/deposit/return` — gateway redirects here after the hosted checkout.
 * We don't trust the redirect; the webhook settles the txn. This page just
 * polls /wallet for a few seconds and then nudges the user back to the wallet.
 */
export function renderDepositReturn(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/wallet" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Deposit</div>
    </div>
    <div class="pad">
      <div class="card center">
        <div class="muted" style="margin-bottom:8px;">Confirming your payment…</div>
        <div id="deposit-return-balance" class="big" style="font-size:30px;">—</div>
        <a href="/wallet" data-link class="btn" style="margin-top:14px;">Back to wallet</a>
      </div>
    </div>
  `;
  const balEl = document.getElementById('deposit-return-balance');
  let cancelled = false;
  (async () => {
    for (let i = 0; i < 6 && !cancelled; i++) {
      try {
        const wallet = await api('/wallet');
        balEl.textContent = formatKobo(wallet.balance_kobo);
      } catch { /* ignore */ }
      await new Promise(r => setTimeout(r, 1000));
    }
  })();
  return () => { cancelled = true; };
}

